import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import NewsListCard from "@/components/feature/NewsListCard";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/lib/seo";
import { useFavorites } from "@/hooks/useFavorites";
import { getMovieById, getNewsById, type Movie, type NewsItem } from "@/lib/media";

type Tab = "media" | "news";

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { movieIds, newsIds, clearAll } = useFavorites();
  const [tab, setTab] = useState<Tab>("media");

  usePageMeta({
    title: "我的收藏 - 枫与铃",
    description: "查看你在枫与铃收藏的影视作品与资讯文章，收藏数据保存在本机浏览器中。",
    keywords: "我的收藏,影视收藏,枫与铃",
  });

  const favoriteMovies = movieIds
    .map((id) => getMovieById(id))
    .filter((item): item is Movie => Boolean(item));
  const favoriteNews = newsIds
    .map((id) => getNewsById(id))
    .filter((item): item is NewsItem => Boolean(item));
  const total = favoriteMovies.length + favoriteNews.length;

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pt-[72px]">
        <section className="border-b border-background-200 bg-background-100/80">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="cursor-pointer hover:text-primary-600">
                首页
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line text-sm" />
              </span>
              <span className="text-foreground-800">我的收藏</span>
            </nav>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  我的收藏
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                  收藏数据保存在本机浏览器中，刷新页面后依然保留。共 {total} 条内容。
                </p>
              </div>
              {total > 0 ? (
                <button
                  type="button"
                  onClick={clearAll}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-delete-bin-6-line text-sm" />
                  </span>
                  清空收藏
                </button>
              ) : null}
            </div>

            <div className="mt-6 flex items-center gap-1 rounded-md bg-background-200/70 p-1 sm:w-fit">
              <button
                type="button"
                onClick={() => setTab("media")}
                className={`flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded px-4 py-1.5 text-sm transition-colors sm:flex-none ${
                  tab === "media"
                    ? "bg-background-50 font-medium text-primary-600"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                影视作品
                <span className="text-[10px] text-foreground-500">{favoriteMovies.length}</span>
              </button>
              <button
                type="button"
                onClick={() => setTab("news")}
                className={`flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded px-4 py-1.5 text-sm transition-colors sm:flex-none ${
                  tab === "news"
                    ? "bg-background-50 font-medium text-primary-600"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                资讯文章
                <span className="text-[10px] text-foreground-500">{favoriteNews.length}</span>
              </button>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          {tab === "media" ? (
            favoriteMovies.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
                {favoriteMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="ri-heart-3-line"
                title="还没有收藏影视作品"
                description="在影视详情页点击「加入我的收藏」，作品就会出现在这里。"
                actionLabel="去逛电影分类"
                onAction={() => navigate("/category/movie")}
              />
            )
          ) : favoriteNews.length > 0 ? (
            <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-2">
              {favoriteNews.map((item) => (
                <NewsListCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="ri-bookmark-line"
              title="还没有收藏资讯"
              description="阅读资讯时点击「收藏这篇」，文章就会保存在本机。"
              actionLabel="去看看资讯"
              onAction={() => navigate("/news")}
            />
          )}
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}